import type { CandidateDetail } from '@/types/candidates';
import { usePermission } from '@/lib/permissions';
import { Link } from '@tanstack/react-router';
import { awaitsAnonymisation, isConsentLocked } from '../lib/consent-actions';

interface CandidateConsentBannerProps {
  candidate: CandidateDetail;
  /** Hide the GDPR link where the GDPR tab is not a route (the board modal). */
  showGdprLink?: boolean;
}

/**
 * HW-1055: says once, above the tabs, why nothing on a candidate who revoked
 * consent can be changed — the profile page and the board modal both show it.
 */
export function CandidateConsentBanner({
  candidate,
  showGdprLink = true,
}: CandidateConsentBannerProps) {
  // The GDPR tab is where they are anonymised — linked only for those who may open it.
  const canOpenGdpr = usePermission('recruitment.candidates.gdpr');
  if (!isConsentLocked(candidate)) return null;
  const pending = awaitsAnonymisation(candidate);

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
      <span>
        <span className="font-medium">Consent revoked.</span>{' '}
        {pending
          ? 'This candidate is read-only until anonymised — anonymising deletes them and all their data.'
          : 'This candidate is read-only.'}
      </span>
      {pending && showGdprLink && canOpenGdpr && (
        <Link
          to="/recruitment/candidates/$id/gdpr"
          params={{ id: candidate.id }}
          className="font-medium underline underline-offset-4"
        >
          Open GDPR
        </Link>
      )}
    </div>
  );
}
